import { z } from 'zod';

/** Sinal de 5% (Carta de Intenções) ou saldo de 95% (Termo de Aceite). */
export const TIPOS_COBRANCA = ['sinal', 'saldo'] as const;
export const tipoCobrancaSchema = z.enum(TIPOS_COBRANCA);
export type TipoCobranca = z.infer<typeof tipoCobrancaSchema>;

/**
 * Formas de pagamento aceitas pelo Asaas que usamos.
 * `UNDEFINED` deixa a escolha (Pix, cartão ou boleto) com o pagador na própria fatura.
 */
export const FORMAS_PAGAMENTO = ['UNDEFINED', 'PIX', 'CREDIT_CARD', 'BOLETO'] as const;
export const formaPagamentoSchema = z.enum(FORMAS_PAGAMENTO);
export type FormaPagamento = z.infer<typeof formaPagamentoSchema>;

/**
 * Situações da cobrança. Espelham os status do Asaas que nos interessam; qualquer
 * outro status recebido pelo webhook é gravado como `OUTRO` e registrado no log.
 */
export const SITUACOES_COBRANCA = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
  'VENCIDA',
  'ESTORNADA',
  'CANCELADA',
  'OUTRO',
] as const;
export const situacaoCobrancaSchema = z.enum(SITUACOES_COBRANCA);
export type SituacaoCobranca = z.infer<typeof situacaoCobrancaSchema>;

export const cobrancaSchema = z.object({
  cobrancaId: z.string(),
  candidatoId: z.string(),
  tipo: tipoCobrancaSchema,
  /** id da cobrança no Asaas (pay_xxx). Chave de conciliação com o webhook. */
  asaasId: z.string(),
  /** id do cliente no Asaas (cus_xxx). */
  asaasClienteId: z.string(),
  /** Valor bruto em centavos. */
  valor: z.number().int().nonnegative(),
  /**
   * Parcela que sai da conta titular via split, em centavos. A conta é da RREVELA,
   * então esta é a parte da Academia (70%). Ver packages/shared/src/split.ts.
   */
  valorSplitAcademia: z.number().int().nonnegative(),
  formaPagamento: formaPagamentoSchema,
  parcelas: z.number().int().positive(),
  vencimento: z.string(), // AAAA-MM-DD
  situacao: situacaoCobrancaSchema,
  /** URL da fatura para enviar ao pagador. */
  urlFatura: z.string().url(),
  /** Valor efetivamente recebido, em centavos — preenchido pelo webhook. */
  valorRecebido: z.number().int().nonnegative().optional(),
  pagoEm: z.string().datetime().optional(),
  criadoEm: z.string().datetime(),
  criadoPor: z.string(),
  atualizadoEm: z.string().datetime(),
});
export type Cobranca = z.infer<typeof cobrancaSchema>;

/** Corpo aceito por POST /candidatos/:id/cobrancas */
export const criarCobrancaSchema = z
  .object({
    tipo: tipoCobrancaSchema,
    formaPagamento: formaPagamentoSchema.default('UNDEFINED'),
    /**
     * Sinal é sempre à vista (Carta, cl. 3.2). Saldo aceita até 12x no cartão
     * (Termo, cl. 3.2) — a API rejeita parcelas > 1 no sinal.
     */
    parcelas: z.number().int().min(1).max(12).default(1),
  })
  .strict();
export type CriarCobranca = z.infer<typeof criarCobrancaSchema>;

export const MAX_PARCELAS_SALDO = 12;

/**
 * Situações em que a cobrança ainda "vale": ou está esperando pagamento, ou já foi paga.
 * Existindo uma dessas, emitir outra do mesmo tipo mandaria dois links de seis dígitos
 * para o mesmo pagador.
 */
export const SITUACOES_EM_ABERTO: readonly SituacaoCobranca[] = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
];

export function cobrancaEmAberto(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return SITUACOES_EM_ABERTO.includes(cobranca.situacao);
}

export function cobrancaJaPaga(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return cobranca.situacao === 'CONFIRMADA' || cobranca.situacao === 'RECEBIDA';
}
